(() => {
  'use strict';

  const ELEMENT_NAME = 'lavinde-homepage-products';

  if (customElements.get(ELEMENT_NAME)) return;

  class LavindeHomepageProducts extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.addEventListener('click', (event) => this.handleClick(event));
      this.injectFixStyles();
      this.cacheDefaultFoundationBenefit();
      this.updateFoundationBenefit(this.chosen);

      this.boundFoundationResize = () => {
        clearTimeout(this.foundationResizeTimer);
        this.foundationResizeTimer = setTimeout(() => this.updateFoundationBenefit(this.chosen), 80);
      };
      window.addEventListener('resize', this.boundFoundationResize, { passive: true });

      if (this.sheet && !this.sheet.id) {
        this.sheet.id = `LavindeFoundationSheet-${Date.now()}`;
      }

      const opener = this.card && this.card.querySelector('[data-foundation-selector-open]');
      if (opener && this.sheet) {
        opener.setAttribute('aria-haspopup', 'dialog');
        opener.setAttribute('aria-controls', this.sheet.id);
      }
    }

    disconnectedCallback() {
      if (this.boundFoundationResize) {
        window.removeEventListener('resize', this.boundFoundationResize);
      }
      clearTimeout(this.foundationResizeTimer);
    }

    injectFixStyles() {
      if (document.getElementById('LavindeCardsFix')) return;

      const style = document.createElement('style');
      style.id = 'LavindeCardsFix';
      style.textContent = `
        .lavinde-homepage-cards .lavinde-card__media,
        .lavinde-homepage-cards .lavinde-card__image-link,
        .lavinde-homepage-cards .lavinde-foundation__default-image,
        .lavinde-homepage-cards .lavinde-foundation__selected-image {
          background: var(--lhpc-card-bg) !important;
        }

        .lavinde-homepage-cards .lavinde-card__image {
          background-color: transparent !important;
          mix-blend-mode: darken !important;
        }

        .lavinde-homepage-cards .lavinde-foundation__button-chevron {
          display: none !important;
        }

        @media screen and (min-width: 750px) {
          .lavinde-homepage-cards .lavinde-foundation-sheet[open] {
            display: block !important;
            position: fixed !important;
            inset: 50% auto auto 50% !important;
            width: min(430px, calc(100vw - 48px)) !important;
            max-height: min(680px, calc(100dvh - 48px)) !important;
            margin: 0 !important;
            transform: translate(-50%, -50%) !important;
            border-radius: 16px !important;
            background: transparent !important;
          }

          .lavinde-homepage-cards .lavinde-foundation-sheet__panel {
            max-height: min(680px, calc(100dvh - 48px)) !important;
            border-radius: 16px !important;
          }

          .lavinde-homepage-cards .lavinde-foundation-sheet__panel > [data-foundation-sheet-picker] {
            padding: 24px !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    async handleClick(event) {
      let target = event.target.closest('[data-foundation-selector-open]');
      if (target && this.contains(target)) {
        event.preventDefault();
        this.openSheet(target);
        return;
      }

      target = event.target.closest('[data-foundation-swatch]');
      if (target && this.contains(target)) {
        event.preventDefault();
        this.selectShade(target);
        return;
      }

      target = event.target.closest('[data-foundation-sheet-add]');
      if (target && this.contains(target)) {
        event.preventDefault();
        await this.addSelectedShade(target);
        return;
      }

      target = event.target.closest('[data-foundation-dialog-close], [data-foundation-continue]');
      if (target && this.contains(target)) {
        event.preventDefault();
        this.closeSheet();
        return;
      }

      target = event.target.closest('[data-foundation-view-cart]');
      if (target && this.contains(target)) {
        event.preventDefault();
        this.openCart();
      }
    }

    openSheet(button) {
      if (!this.sheet) return;

      this.focusReturn = button;
      this.showSheetStep(false);

      try {
        if (this.sheet.showModal) this.sheet.showModal();
        else this.sheet.setAttribute('open', '');
      } catch (error) {
        this.sheet.setAttribute('open', '');
      }

      document.documentElement.classList.add('lavinde-dialog-open');

      setTimeout(() => {
        const focusTarget =
          this.sheet.querySelector('[data-foundation-swatch][aria-checked="true"]') ||
          this.sheet.querySelector('[data-foundation-swatch]') ||
          this.sheet.querySelector('[data-foundation-dialog-close]');

        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }, 30);
    }

    closeSheet() {
      if (!this.sheet) return;

      if (this.sheet.open && this.sheet.close) this.sheet.close();
      else this.sheet.removeAttribute('open');

      document.documentElement.classList.remove('lavinde-dialog-open');

      if (this.focusReturn) {
        const returnTarget = this.focusReturn;
        this.focusReturn = null;
        setTimeout(() => returnTarget.focus({ preventScroll: true }), 30);
      }
    }

    showSheetStep(success) {
      if (!this.sheet) return;

      const picker = this.sheet.querySelector('[data-foundation-sheet-picker]');
      const successView = this.sheet.querySelector('[data-foundation-sheet-success]');
      const error = this.sheet.querySelector('[data-foundation-sheet-error]');

      if (picker) picker.hidden = !!success;
      if (successView) successView.hidden = !success;
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      this.syncSheet(this.chosen);
    }

    selectShade(swatch) {
      if (!this.card || !swatch) return;

      const shadeKey = swatch.dataset.shadeKey;

      this.card.querySelectorAll('[data-foundation-swatch]').forEach((item) => {
        const isSelected = item.dataset.shadeKey === shadeKey;
        item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        item.tabIndex = isSelected ? 0 : -1;
      });

      this.card.querySelectorAll('[data-foundation-default-image]').forEach((image) => {
        image.hidden = true;
      });

      this.card.querySelectorAll('[data-foundation-image]').forEach((image) => {
        image.hidden = image.dataset.foundationImage !== shadeKey;
      });

      this.card.querySelectorAll('[data-foundation-award]').forEach((award) => {
        award.hidden = award.dataset.foundationAward !== shadeKey;
      });

      this.card.querySelectorAll('[data-foundation-rating]').forEach((rating) => {
        rating.hidden = rating.dataset.foundationRating !== shadeKey;
      });

      this.card.querySelectorAll('[data-foundation-product-link]').forEach((link) => {
        if (swatch.dataset.productUrl) link.href = swatch.dataset.productUrl;
      });

      this.setText(this.card, '[data-foundation-selected-label]', this.shadeTitle(swatch));
      this.updateFoundationBenefit(swatch);
      this.setText(this.card, '[data-foundation-price]', swatch.dataset.price || '');
      this.setOptionalText(this.card, '[data-foundation-compare]', swatch.dataset.comparePrice || '');
      this.setOptionalText(
        this.card,
        '[data-foundation-saving]',
        swatch.dataset.savingsPercent ? `SPAR ${swatch.dataset.savingsPercent} %` : ''
      );

      this.card.classList.add('has-selected-shade');
      this.card.dataset.shadeSelected = 'true';
      this.card.dataset.productId = swatch.dataset.productId || '';
      this.card.dataset.productHandle = swatch.dataset.productHandle || '';
      this.card.dataset.productTitle = swatch.dataset.productTitle || '';
      this.card.dataset.variantId = swatch.dataset.variantId || '';

      this.card.querySelectorAll('[data-foundation-form]').forEach((form) => {
        form.hidden = form.dataset.foundationForm !== shadeKey;
      });

      this.syncSheet(swatch);
    }

    cacheDefaultFoundationBenefit() {
      const benefit = this.card && this.card.querySelector('[data-foundation-benefit]');
      if (!benefit) return;

      if (!benefit.dataset.foundationDefaultBenefit) {
        benefit.dataset.foundationDefaultBenefit = this.clean(benefit.textContent);
      }
    }

    updateFoundationBenefit(swatch) {
      const benefit = this.card && this.card.querySelector('[data-foundation-benefit]');
      if (!benefit) return;

      if (!benefit.dataset.foundationDefaultBenefit) {
        benefit.dataset.foundationDefaultBenefit = this.clean(benefit.textContent);
      }

      const defaultText = this.clean(benefit.dataset.foundationDefaultBenefit);
      const shadeText = swatch && swatch.dataset ? this.clean(swatch.dataset.shadeDescription) : '';
      const nextText = shadeText || defaultText;

      benefit.textContent = nextText;
      benefit.hidden = !nextText;
    }

    syncSheet(swatch) {
      if (!this.sheet) return;

      const selected = this.sheet.querySelector('[data-foundation-sheet-selected]');
      const undertone = this.sheet.querySelector('[data-foundation-sheet-undertone]');
      const addButton = this.sheet.querySelector('[data-foundation-sheet-add]');
      const addLabel = this.sheet.querySelector('[data-foundation-sheet-add-label]');
      const addPrice = this.sheet.querySelector('[data-foundation-sheet-add-price]');

      if (!swatch) {
        this.updateFoundationBenefit(null);
        if (selected) selected.textContent = this.dataset.selectLabel || 'Vælg variant';
        if (undertone) {
          undertone.textContent = '';
          undertone.hidden = true;
        }
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = this.dataset.addLabel || 'Læg i kurv';
        if (addPrice) addPrice.textContent = '';
        return;
      }

      const isAvailable = swatch.dataset.available === 'true';

      if (selected) selected.textContent = this.shadeTitle(swatch);
      if (undertone) {
        undertone.textContent = swatch.dataset.undertone ? `${swatch.dataset.undertone} undertone` : '';
        undertone.hidden = !swatch.dataset.undertone;
      }
      if (addButton) addButton.disabled = !isAvailable;
      if (addLabel) addLabel.textContent = isAvailable ? (this.dataset.addLabel || 'Læg i kurv') : (this.dataset.soldOutLabel || 'Udsolgt');
      if (addPrice) addPrice.textContent = isAvailable ? (swatch.dataset.price || '') : '';
    }

    async addSelectedShade(button) {
      const swatch = this.chosen;

      if (!swatch) {
        this.showError(this.dataset.selectLabel || 'Vælg variant');
        return;
      }

      if (swatch.dataset.available !== 'true') {
        this.showError(this.dataset.soldOutLabel || 'Udsolgt');
        return;
      }

      const variantId = swatch.dataset.variantId;
      if (!variantId) {
        this.showError(this.dataset.errorLabel || 'Varen kunne ikke lægges i kurven. Prøv igen.');
        return;
      }

      const addLabel = this.sheet.querySelector('[data-foundation-sheet-add-label]');
      button.disabled = true;
      if (addLabel) addLabel.textContent = this.dataset.addingLabel || 'Tilføjer…';
      this.showError('');

      try {
        const response = await fetch(this.ensureJsonUrl(this.dataset.cartAddUrl, '/cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (!response.ok) throw new Error('Add to cart failed');
        await response.json().catch(() => null);

        const cart = await fetch(this.ensureJsonUrl(this.dataset.cartUrl, '/cart.js'), {
          headers: { Accept: 'application/json' }
        }).then((cartResponse) => (cartResponse.ok ? cartResponse.json() : null));

        this.showSuccess(swatch, cart);
        document.dispatchEvent(new CustomEvent('theme:cart:change', {
          bubbles: true,
          detail: { cart }
        }));
      } catch (error) {
        this.showError(this.dataset.errorLabel || 'Varen kunne ikke lægges i kurven. Prøv igen.');
      } finally {
        this.syncSheet(swatch);
      }
    }

    showSuccess(swatch, cart) {
      this.showSheetStep(true);
      this.setText(this.sheet, '[data-foundation-success-shade]', this.shadeTitle(swatch));

      const cartCount = this.sheet.querySelector('[data-foundation-cart-count]');
      if (cartCount) cartCount.textContent = cart && cart.item_count ? `(${cart.item_count})` : '';
    }

    openCart() {
      this.closeSheet();

      setTimeout(() => {
        const selectors = [
          '[data-drawer-toggle="drawer-cart"]',
          '[data-drawer-toggle="cart"]',
          '[data-cart-drawer-toggle]',
          '[data-cart-toggle]',
          '[data-header-cart]',
          'a[href="/cart"]',
          'a[href$="/cart"]'
        ];

        for (const selector of selectors) {
          const trigger = document.querySelector(selector);
          if (trigger && trigger.click) {
            trigger.click();
            return;
          }
        }

        window.location.href = this.dataset.cartUrl || '/cart';
      }, 80);
    }

    showError(message) {
      const error = this.sheet && this.sheet.querySelector('[data-foundation-sheet-error]');
      if (!error) return;

      error.textContent = message || '';
      error.hidden = !message;
    }

    setText(root, selector, value) {
      const element = root && root.querySelector(selector);
      if (element) element.textContent = value || '';
    }

    setOptionalText(root, selector, value) {
      const element = root && root.querySelector(selector);
      if (!element) return;

      element.textContent = value || '';
      element.hidden = !value;
    }

    shadeTitle(swatch) {
      return [swatch.dataset.shadeCode || '', swatch.dataset.shadeName || ''].filter(Boolean).join(' · ');
    }

    ensureJsonUrl(url, fallback) {
      const base = url || fallback;
      return base && base.indexOf('.js') < 0 ? `${base.endsWith('/') ? base.slice(0, -1) : base}.js` : base;
    }

    clean(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    get card() {
      return this.querySelector('[data-foundation-card]');
    }

    get sheet() {
      return this.querySelector('[data-foundation-dialog]');
    }

    get chosen() {
      return this.card && this.card.querySelector('[data-foundation-swatch][aria-checked="true"]');
    }
  }

  customElements.define(ELEMENT_NAME, LavindeHomepageProducts);
})();

(() => {
  'use strict';

  if (window.__lavindeBenefitSwitcherV1) return;
  window.__lavindeBenefitSwitcherV1 = 1;

  const selector = '[data-lavinde-benefit-auto]';
  const clean = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  const apply = (root = document) => {
    const nodes = root && root.querySelectorAll ? root.querySelectorAll(selector) : document.querySelectorAll(selector);

    nodes.forEach((element) => {
      const desktop = clean(element.dataset.benefitDesktop);
      const mobile = clean(element.dataset.benefitMobile) || desktop;
      const width = window.innerWidth || document.documentElement.clientWidth || 1024;

      if (width < 750) {
        setText(element, mobile || desktop);
        return;
      }

      if (width >= 990) {
        setText(element, desktop || mobile);
        return;
      }

      setText(element, desktop || mobile);
      requestAnimationFrame(() => {
        if (!mobile || mobile === desktop) return;
        const overflows = element.scrollHeight > element.clientHeight + 1 || element.scrollWidth > element.clientWidth + 1;
        if (overflows) setText(element, mobile);
      });
    });
  };

  let timer = 0;
  const schedule = (root = document) => {
    clearTimeout(timer);
    timer = setTimeout(() => apply(root), 70);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => apply());
  } else {
    apply();
  }

  window.addEventListener('load', () => {
    apply();
    setTimeout(apply, 400);
  });
  window.addEventListener('resize', () => schedule());
  document.addEventListener('shopify:section:load', (event) => schedule(event.target));
  document.addEventListener('shopify:block:select', () => schedule());
  document.addEventListener('shopify:block:deselect', () => schedule());
})();

(() => {
  'use strict';

  if (window.__lavindeFoundationAwardSwitcherV1) return;
  window.__lavindeFoundationAwardSwitcherV1 = 1;

  const sync = (card, key) => {
    if (!card || !key) return;
    card.querySelectorAll('[data-foundation-award]').forEach((award) => {
      award.hidden = award.dataset.foundationAward !== key;
    });
  };

  const init = (root = document) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-foundation-card]').forEach((card) => {
      const swatch = card.querySelector('[data-foundation-swatch][aria-checked="true"]') || card.querySelector('[data-foundation-swatch]');
      if (swatch) sync(card, swatch.dataset.shadeKey);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-foundation-swatch]');
    if (!swatch) return;

    const card = swatch.closest('[data-foundation-card]');
    if (card) setTimeout(() => sync(card, swatch.dataset.shadeKey), 0);
  });

  document.addEventListener('shopify:section:load', (event) => setTimeout(() => init(event.target), 60));
})();
